import { Modal, ModalProps, Paper } from '@mui/material';

import { isOverflown } from '@app/utils/isOverflown';
import cx from 'classnames';
import { ComponentProps, useRef } from 'react';
import { Slide } from '../Animations/Slide';
import { CloseButton } from '../CloseButton/CloseButton';
import { TextReplacer } from '../TextReplacer/TextReplacer';

interface Props extends ModalProps {
  withCloseButton?: boolean;
  /** The close button sits on top of the modal's own header band — pick a colour that reads on it. */
  closeButtonColor?: ComponentProps<typeof CloseButton>['btnColor'];
  maxWidth?: string;
  withDieter?: boolean;
  /**
   * Marks a modal the app opens on its own initiative (a milestone reminder, a
   * changed-questionnaire notice) rather than in response to a user action.
   *
   * The E2E suite dismisses these automatically wherever they land: they arrive
   * asynchronously and their backdrop swallows clicks, which otherwise shows up
   * as an unrelated action failing with "subtree intercepts pointer events".
   * Modals opened *by* a user action must not set this — the suite would close
   * the very dialog a test just opened.
   */
  unsolicited?: boolean;
}

export function NotificationModal({
  open,
  onClose,
  children,
  maxWidth = '550px',
  withCloseButton = true,
  closeButtonColor = 'primary',
  withDieter = false,
  unsolicited = false,
  ...props
}: Props) {
  const modalRef = useRef<HTMLDivElement>(null);
  const overFlow = isOverflown(modalRef.current);

  return (
    <Modal open={Boolean(open)} onClose={onClose} {...props}>
      <Slide in={Boolean(open)}>
        <TextReplacer>
          <Paper
            className={cx(
              `absolute p-[10px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:w-5/6 w-[95vw] max-h-[85vh] bg-white shadow-md pointer-events-auto border-none overflow-y-auto z-1400`,
              { overFlow }
            )}
            style={{ maxWidth }}
            elevation={10}
            ref={modalRef}
            data-testid={unsolicited ? 'unsolicited-modal' : undefined}
          >
            <div className="relative">
              {withCloseButton && (
                <CloseButton
                  onClick={onClose as () => void}
                  btnColor={closeButtonColor}
                  data-testid="notification-modal-close"
                />
              )}
              {children}
            </div>
          </Paper>
        </TextReplacer>
      </Slide>
    </Modal>
  );
}
